import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { StarIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline';
import { 
  ArrowLeftIcon, 
  PlayIcon, 
  ClockIcon, 
  UserGroupIcon, 
  AcademicCapIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  BookOpenIcon,
  TrophyIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCourseProgress } from '../contexts/CourseProgressContext';

const CourseDetailPage = () => {
  const { identifier } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    modules: contextModules,
    isModuleCompleted,
    isPreTestCompleted: contextIsPreTestCompleted,
    fetchCourseProgressAndModules,
    getFirstIntroModule,
    getPreTestModule,
    isLoading: progressLoading,
    error: progressError,
    resetProgressForCourse,
    completedModules,
  } = useCourseProgress();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('deskripsi');
  const [isEnrolling, setIsEnrolling] = useState(false);

  const [certificateEligible, setCertificateEligible] = useState(false);
  const [eligibilityChecked, setEligibilityChecked] = useState(false);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [eligibilityError, setEligibilityError] = useState('');
  const [eligibilityReasons, setEligibilityReasons] = useState([]);

  // Reviews state and pagination
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalReviews, setTotalReviews] = useState(0);
  const [averageRating, setAverageRating] = useState(0);
  const reviewsPerPage = 10;

  const fetchReviews = async (courseId, pageNum) => {
    setLoadingReviews(true);
    try {
      const response = await api.get(`/courses/${courseId}/reviews`, {
        params: {
          page: pageNum,
          limit: reviewsPerPage,
        },
      });
      if (response.data && response.data.status === 'success') {
        if (pageNum === 1) {
          setReviews(response.data.data.reviews);
        } else {
          setReviews((prev) => [...prev, ...response.data.data.reviews]);
        }
        setTotalReviews(response.data.data.total);
        setAverageRating(response.data.data.averageRating || 0);
        setHasMore(response.data.data.reviews.length === reviewsPerPage);
        setPage(pageNum);
      }
    } catch (err) {
      console.error("Error fetching reviews:", err);
    } finally {
      setLoadingReviews(false);
    }
  };

  useEffect(() => {
  const fetchDetailsAndProgress = async () => {
      if (identifier) {
        setLoading(true);
        setError('');
        try {
          const response = await api.get(`/courses/${identifier}`);
          if (
            response.data &&
            response.data.status === 'success' &&
            response.data.data &&
            response.data.data.course
          ) {
            const fetchedCourse = response.data.data.course;
            setCourse({
              ...fetchedCourse,
              deskripsiLengkap: fetchedCourse.deskripsi || "Deskripsi lengkap tidak tersedia.",
              syaratKetentuan: fetchedCourse.syaratDanKetentuan || fetchedCourse.syarat_ketentuan || "Syarat & ketentuan tidak tersedia.",
              shortDescriptionSidebar: fetchedCourse.deskripsi_singkat_sidebar ||
                                       (typeof fetchedCourse.deskripsi === 'string' ? fetchedCourse.deskripsi.substring(0, 100) + "..." : "Info singkat kursus.")
            });
            if (fetchedCourse.id) {
              await fetchReviews(fetchedCourse.id, 1);
              if (user) {
                await fetchCourseProgressAndModules(fetchedCourse.id);
              }
            }
          } else {
            setError('Gagal memuat detail kursus.');
          }
        } catch (err) {
          console.error('Error fetching course details:', err);
          setError('Terjadi kesalahan saat memuat detail kursus.');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchDetailsAndProgress();
  }, [identifier, user, fetchCourseProgressAndModules]);

  useEffect(() => {
    const checkCertificateEligibility = async () => {
      if (user && course && course.needsPreTest && course.needsPostTest && !eligibilityChecked) {
        setEligibilityLoading(true);
        setEligibilityError('');
        try {
          // Use the identifier from URL params instead of course.id
          const response = await api.get(`/courses/${identifier}/certificate/eligibility`);
          if (response.data && response.data.status === 'success') {
            setCertificateEligible(response.data.eligible);
            setEligibilityReasons(response.data.reasons || []);
          }
        } catch (err) {
          console.error('Error checking certificate eligibility:', err);
          setEligibilityError('Gagal memeriksa kelayakan sertifikat.');
        } finally {
          setEligibilityLoading(false);
          setEligibilityChecked(true);
        }
      }
    };

    checkCertificateEligibility();
  }, [user, course, eligibilityChecked, identifier]);

  const handleDownloadCertificate = async () => {
    if (!course || !user) return;
    
    try {
      // Use the identifier from URL params instead of course.id
      const response = await api.get(`/courses/${identifier}/certificate/download`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sertifikat-${course.judul.replace(/\s+/g, '-').toLowerCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading certificate:', err);
      alert('Gagal mengunduh sertifikat. Silakan coba lagi.');
    }
  };

  // Button logic
  let buttonText = 'Memuat...';
  let buttonAction = () => {};

  if (loading) {
    buttonText = 'Memuat...';
  } else if (course) {
    if (course.needsPreTest || course.needsPostTest) {
      buttonText = 'Mulai Course';
    } else {
      buttonText = 'Lihat Course';
    }
    buttonAction = () => {
      setIsEnrolling(true);
      if (!course || (!course.id && !course.slug)) {
        setIsEnrolling(false);
        alert('Detail kursus belum dimuat sepenuhnya atau tidak valid.');
        return;
      }
      try {
        const navigateTo = course.slug
          ? `/course/${course.slug}/moduleslist`
          : `/course/${course.id}/moduleslist`;
        navigate(navigateTo);
      } catch (err) {
        console.error('Error navigating to modules list:', err);
        alert('Gagal memulai kursus. Silakan coba lagi.');
      } finally {
        setIsEnrolling(false);
      }
    };
  } else if (error || progressError) {
    buttonText = 'Gagal Memuat Kursus';
    buttonAction = () => window.location.reload();
  }

  if (loading && !course) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat detail kursus...</p>
        </div>
      </div>
    );
  }

  if (error && !course) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">Terjadi Kesalahan</h3>
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!course && !loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpenIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Kursus Tidak Ditemukan</h3>
            <p className="text-gray-600">Kursus yang Anda cari tidak tersedia.</p>
          </div>
        </div>
      </div>
    );
  }

  if (progressError && course) {
    console.error('Course Progress Error:', progressError);
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/"
            className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200 mb-6"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            <span className="font-medium">Kembali ke Halaman Utama</span>
          </Link>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="lg:w-2/3">
            {/* Course Image */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
              <img 
                src={course.imageSrc} 
                alt={course.judul} 
                className="w-full h-64 md:h-80 object-cover" 
              />
            </div>

            {/* Course Title */}
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4 leading-tight">
                {course.judul}
              </h1>
              
              {/* Course Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                <div className="text-center p-4 bg-blue-50 rounded-xl">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <BookOpenIcon className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="text-lg font-semibold text-blue-600">
                    {contextModules ? contextModules.length : 0}
                  </div>
                  <div className="text-xs text-gray-600">Modul</div>
                </div>
                

                
                <div className="text-center p-4 bg-orange-50 rounded-xl">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <TrophyIcon className="w-4 h-4 text-orange-600" />
                  </div>
                  <div className="text-lg font-semibold text-orange-600">
                    {course.needsPostTest ? 'Ya' : 'Tidak'}
                  </div>
                  <div className="text-xs text-gray-600">Sertifikat</div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="border-b border-gray-200">
                <nav className="flex" aria-label="Tabs">
                  <button
                    onClick={() => setActiveTab('deskripsi')}
                    className={`flex-1 px-6 py-4 text-sm font-medium transition-colors duration-200 ${
                      activeTab === 'deskripsi'
                        ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <DocumentTextIcon className="w-4 h-4 mr-2 inline" />
                    Deskripsi
                  </button>
                  <button
                    onClick={() => setActiveTab('syarat')}
                    className={`flex-1 px-6 py-4 text-sm font-medium transition-colors duration-200 ${
                      activeTab === 'syarat'
                        ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <CheckCircleIcon className="w-4 h-4 mr-2 inline" />
                    Syarat & Ketentuan
                  </button>
                </nav>
              </div>
              
              <div className="p-6 md:p-8">
                {activeTab === 'deskripsi' && (
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                      <DocumentTextIcon className="w-6 h-6 mr-2 text-blue-600" />
                      Deskripsi Kursus
                    </h3>
                    <div className="prose max-w-none text-gray-700 leading-relaxed">
                      {course.deskripsiLengkap && course.deskripsiLengkap !== "Deskripsi lengkap tidak tersedia." ? (
                        <div 
                          className="space-y-4"
                          dangerouslySetInnerHTML={{ 
                            __html: course.deskripsiLengkap
                              .replace(/\n/g, '<br>')
                              .replace(/•/g, '<span class="text-blue-500">•</span>')
                          }} 
                        />
                      ) : (
                        <p className="text-gray-600 italic">Deskripsi lengkap tidak tersedia.</p>
                      )}
                    </div>
                  </div>
                )}
                
                {activeTab === 'syarat' && (
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                      <CheckCircleIcon className="w-6 h-6 mr-2 text-blue-600" />
                      Syarat & Ketentuan
                    </h3>
                    <div className="prose max-w-none text-gray-700 leading-relaxed">
                      {course.syaratKetentuan && course.syaratKetentuan !== "Syarat & ketentuan tidak tersedia." ? (
                        course.syaratKetentuan.split('\n\n').map((section, sectionIndex) => {
                          const numberedMatch = section.match(/^(\d+)\.\s*(.+)$/);
                          if (numberedMatch) {
                            const number = numberedMatch[1];
                            const title = numberedMatch[2];
                            const content = section.substring(numberedMatch[0].length).trim();
                            return (
                              <div key={sectionIndex} className="space-y-4 mb-6">
                                <div className="flex items-start space-x-3">
                                  <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold">{number}</span>
                                  <h4 className="font-semibold text-gray-800 mb-2">{title}</h4>
                                </div>
                                {content.split('\n').map((line, lineIndex) => {
                                  if (line.startsWith('•')) {
                                    return (
                                      <div key={lineIndex} className="flex items-start space-x-3 ml-9">
                                        <span className="flex-shrink-0 text-blue-500">•</span>
                                        <p className="flex-1 text-gray-600">{line.substring(1).trim()}</p>
                                      </div>
                                    );
                                  }
                                  return <p key={lineIndex} className="text-gray-600 ml-9">{line.trim()}</p>;
                                })}
                              </div>
                            );
                          }
                          return <p key={sectionIndex} className="mb-4">{section.replace(/\n/g, '<br>')}</p>;
                        })
                      ) : (
                        <p className="text-gray-600 italic">Syarat & ketentuan tidak tersedia.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:w-1/3">
            <div className="sticky top-8">
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AcademicCapIcon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Mulai Belajar</h3>
                  <p className="text-gray-600 text-sm">{course.shortDescriptionSidebar}</p>
                </div>

                <button
                  onClick={buttonAction}
                  disabled={loading || isEnrolling}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold py-3 px-6 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                >
                  {isEnrolling ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                      Memproses...
                    </>
                  ) : (
                    <>
                      <PlayIcon className="w-5 h-5 mr-2" />
                      {buttonText}
                    </>
                  )}
                </button>

                {user && eligibilityChecked && course.needsPreTest && course.needsPostTest && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <TrophyIcon className="w-5 h-5 mr-2 text-yellow-600" />
                      Sertifikat
                    </h3>
                    {eligibilityLoading && (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500 mr-2"></div>
                        <span className="text-sm text-gray-600">Memeriksa kelayakan...</span>
                      </div>
                    )}
                    {eligibilityError && (
                      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                        <div className="flex items-center">
                          <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mr-3">
                            <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />
                          </div>
                          <p className="text-sm text-red-700 font-medium">{eligibilityError}</p>
                        </div>
                      </div>
                    )}
                    {!eligibilityLoading && !eligibilityError && (
                      certificateEligible ? (
                        <button
                          onClick={handleDownloadCertificate}
                          className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold py-3 px-4 rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center"
                        >
                          <TrophyIcon className="w-5 h-5 mr-2" />
                          Unduh Sertifikat
                        </button>
                      ) : (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                          <p className="text-sm text-yellow-800 mb-2">Anda belum memenuhi syarat untuk sertifikat.</p>
                          {eligibilityReasons.length > 0 && (
                            <ul className="list-disc list-inside text-xs text-yellow-700 space-y-1">
                              {eligibilityReasons.map((reason, index) => (
                                <li key={index}>{reason}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-16">
          {/* Header with enhanced styling */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-8 border border-blue-100">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="mb-4 md:mb-0">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Ratings & Reviews</h2>
                <p className="text-gray-600">Apa kata peserta tentang kursus ini</p>
              </div>
              <div className="flex items-center bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-center mr-4">
                  {[...Array(5)].map((_, index) =>
                    index < Math.round(averageRating) ? (
                      <StarIcon key={index} className="h-6 w-6 text-yellow-400" />
                    ) : (
                      <StarIconOutline key={index} className="h-6 w-6 text-yellow-400" />
                    )
                  )}
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{averageRating.toFixed(1)}</div>
                  <div className="text-sm text-gray-600">out of 5</div>
                  <div className="text-xs text-gray-500">({totalReviews} reviews)</div>
                </div>
              </div>
            </div>
          </div>

          {/* Reviews Grid */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {reviews.map((review) => (
              <div
                key={review.id}
                className="bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="p-6">
                  {/* Review Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                        {review.user.namaLengkap.charAt(0).toUpperCase()}
                      </div>
                      <div className="ml-3">
                        <h3 className="font-semibold text-gray-900">{review.user.namaLengkap}</h3>
                        <div className="flex items-center mt-1">
                          {[...Array(5)].map((_, index) =>
                            index < review.rating ? (
                              <StarIcon key={index} className="h-4 w-4 text-yellow-400" />
                            ) : (
                              <StarIconOutline key={index} className="h-4 w-4 text-yellow-400" />
                            )
                          )}
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-full">
                      {new Date(review.createdAt).toLocaleDateString('id-ID', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                  
                  {/* Review Content */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-gray-700 leading-relaxed italic">"{review.reviewText}"</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Loading State */}
          {loadingReviews && (
            <div className="text-center py-12">
              <div className="inline-flex items-center px-4 py-2 bg-blue-50 rounded-lg">
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-blue-500 mr-3"></div>
                <span className="text-blue-700">Memuat ulasan...</span>
              </div>
            </div>
          )}

          {/* Load More Button */}
          {hasMore && !loadingReviews && (
            <div className="text-center mt-12">
              <button
                onClick={() => fetchReviews(course.id, page + 1)}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Muat Lebih Banyak Ulasan
              </button>
            </div>
          )}

          {/* No Reviews State */}
          {!loadingReviews && reviews.length === 0 && (
            <div className="text-center py-12">
              <div className="bg-gray-50 rounded-2xl p-8">
                <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <StarIconOutline className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Belum ada ulasan</h3>
                <p className="text-gray-600">Jadilah yang pertama memberikan ulasan untuk kursus ini!</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseDetailPage;
